import {
  convert_char,
  convert_types,
  utf_code,
  assist_digit,
  textarea_default_row,
  link_setting,
  fm_types,
  postcode_index,
  postcode_splitter,
  time_splitter,
} from './Const';
import Moment from 'moment';

/**
 * データ変換
 * @param convert_type 変換タイプ
 * @param value 変換するデータ
 * @return string  変換後のデータ
 */
export const convertData = (convert_type: string, value: string | null): string => {
  if (value === null) {
    value = '';
  }
  if (convert_type === convert_types.add_comma) {
    return convertToAddComma(value);
  } else if (convert_type === convert_types.change_numeric) {
    return convertToNumeric(value);
  } else if (convert_type === convert_types.add_decimal_point) {
    return convertToDecimal(value);
  } else if (convert_type === convert_types.change_zenkaku_kana) {
    return convertToFullKana(value);
  } else if (convert_type === convert_types.change_hankaku_kana) {
    return convertToHalfKana(value);
  } else if (convert_type === convert_types.change_zenkaku) {
    return convertHalfToFull(value);
  } else if (convert_type === convert_types.change_account_holder_zenkakuKana) {
    return convertHaltToAccountHolderZenkakuKana(value);
  } else if (convert_type === convert_types.change_numeric_hyphen) {
    return convertToNumericHyphen(value);
  } else if (convert_type === convert_types.allow_minus) {
    return signedIntegerAddComma(value);
  } else {
    return value;
  }
};

/**
 * 数値カンマ付与
 * @param original 変換するデータ
 * @return string  変換後のデータ
 */
const convertToAddComma = (original: string): string => {
  let result = original;

  // 数値に変換
  result = convertToNumeric(result);

  if (result !== '') {
    // 3桁区切り
    result = new Intl.NumberFormat().format(Number(result));
  }
  return result;
};

/**
 * 数値カンマ付与（マイナス付を許容する）
 * @param original 変換するデータ
 * @return string  変換後のデータ
 */
const signedIntegerAddComma = (original: string): string => {
  let result = convertToNumeric(original);
  if (result !== '') {
    // 3桁区切りでカンマを付与
    result = new Intl.NumberFormat().format(Number(result));
  }
  // 入力値の最初にマイナスがあれば、再度マイナスを付与する
  return /^[-－﹣−‐⁃‑‒–—﹘―⎯⏤─━ー]/.test(original) ? '-' + result : result;
};

/**
 * 数値に変換
 * @param original 変換するデータ
 * @return string  変換後のデータ
 */
const convertToNumeric = (original: string): string => {
  let result = String(original);

  // 全角数字→半角数字
  result = convertFullNumericToHalfNumeric(result);
  // 数字(0～9)以外を除去して
  result = result.replace(/[^\d]/g, '');

  return result;
};

/**
 *　半角数値、半角ハイフンに変換
 * @param original 変換するデータ
 * @return string  変換後のデータ
 */
const convertToNumericHyphen = (original: string): string => {
  let result = String(original);

  // 全角数字→半角数字
  result = convertFullNumericToHalfNumeric(result);
  // 全角ハイフン→半角ハイフン
  result = result.replace(/[-－﹣−‐⁃‑‒–—﹘―⎯⏤─━ー]/g, '-');
  // 数字(0～9)、半角ハイフン以外を除去
  result = result.replace(/[^\d^\-]/g, '');

  return result;
};

/**
 * 全角数字→半角数字変換
 * @param original 変換するデータ
 * @return string  変換後のデータ
 */
const convertFullNumericToHalfNumeric = (original: string): string => {
  let result = '';

  for (let i = 0; i < original.length; i++) {
    let code_original = original.charCodeAt(i);

    if (
      code_original >= utf_code.zenkaku_numeric_start &&
      code_original <= utf_code.zenkaku_numeric_end
    ) {
      result += String.fromCharCode(code_original - utf_code.zenkaku_numeric_convert);
    } else {
      result += original.charAt(i);
    }
  }

  return result;
};

/**
 * 全角ドット→半角ドット変換
 * @param original 変換するデータ
 * @return string  変換後のデータ
 */
const convertFullDotToHalfDot = (original: string): string => {
  return original.replace(/．/g, '.');
};

/**
 * 小数書式変換
 * @param original 変換するデータ
 * @return string  変換後のデータ
 */
const convertToDecimal = (original: string): string => {
  let result = String(original);

  // 全角数字,小数点→半角数字,小数点
  result = convertFullNumericToHalfNumeric(result);
  result = convertFullDotToHalfDot(result);

  // 数字(0～9,ドット)以外を除去
  result = result.replace(/[^\d^\.]/g, '');
  return result;
};

/**
 * 全角カナ変換
 * @param original 変換するデータ
 * @return string  変換後のデータ
 */
const convertToFullKana = (original: string): string => {
  return convertHalfKanaToFullKana(convertHiraganaToFullKana(original));
};

/**
 * 全角ひらがな→全角カナ変換
 * @param original 変換するデータ
 * @return string  変換後のデータ
 */
const convertHiraganaToFullKana = (original: string): string => {
  let result = '';

  for (let i = 0; i < original.length; i++) {
    let code_original = original.charCodeAt(i);

    if (
      code_original >= utf_code.zenkaku_hiragana_start &&
      code_original <= utf_code.zenkaku_hiragana_end
    ) {
      // 全角ひらがな
      result += String.fromCharCode(code_original + utf_code.zenkaku_hiragana_convert);
    } else {
      result += original.charAt(i);
    }
  }

  return result;
};

/**
 * 半角ｶﾅ→全角カナ変換
 * @param original 変換するデータ
 * @return string  変換後のデータ
 */
const convertHalfKanaToFullKana = (original: string): string => {
  let result = '';
  let len = original.length;

  let reg_exp_base_dakuon = new RegExp('[' + convert_char.hankaku_kana_dakuon_base + ']');
  let reg_exp_base_handakuon = new RegExp('[' + convert_char.hankaku_kana_handakuon_base + ']');

  for (let i = 0; i < len; i++) {
    let c = original.charAt(i);
    let code_original = c.charCodeAt(0);

    if (code_original >= 0xff61 && code_original <= 0xff9f) {
      // 半角ｶﾅ
      let dakuon_or_handakuon = false;

      if (c.match(reg_exp_base_dakuon) != null) {
        if (i < len - 1) {
          // 次の文字
          let code_next = original.charCodeAt(i + 1);

          if (code_next == utf_code.hankaku_dakuten) {
            // 濁点あり
            dakuon_or_handakuon = true;
            i++;

            let index = convert_char.hankaku_kana_dakuon_base.indexOf(c);
            result += convert_char.zenkaku_kana_dakuon.charAt(index);
          }
        }
      }

      if (!dakuon_or_handakuon) {
        if (c.match(reg_exp_base_handakuon) != null) {
          if (i < len - 1) {
            // 次の文字
            let code_next = original.charCodeAt(i + 1);
            if (code_next == utf_code.hankaku_handakuten) {
              // 半濁点あり
              dakuon_or_handakuon = true;
              i++;

              let index = convert_char.hankaku_kana_handakuon_base.indexOf(c);
              result += convert_char.zenkaku_kana_handakuon.charAt(index);
            }
          }
        }
      }

      if (!dakuon_or_handakuon) {
        let index = convert_char.hankaku_kana.indexOf(c);
        result += convert_char.zenkaku_kana.charAt(index);
      }
    } else if (c.match(convert_char.hankaku_space)) {
      //半角スペースの場合全角に変換
      result += convert_char.zenkaku_space;
    } else {
      result += c;
    }
  }

  return result;
};

/**
 * 半角ｶﾅ変換
 * @param original 変換するデータ
 * @return string  変換後のデータ
 */
const convertToHalfKana = (original: string): string => {
  return convertSymbolToHalf(convertFullKanaToHalfKana(convertHiraganaToHalfKana(original)));
};

/**
 * 全角カナ→半角ｶﾅ変換
 * @param original 変換するデータ
 * @return string  変換後のデータ
 */
const convertFullKanaToHalfKana = (original: string): string => {
  let result = original;

  // 濁点、半濁点なしの文字を置換
  for (let i = 0; i < convert_char.zenkaku_kana.length; i++) {
    let reg_exp = new RegExp(convert_char.zenkaku_kana.charAt(i), 'g');
    result = result.replace(reg_exp, convert_char.hankaku_kana.charAt(i));
  }

  // 濁点ありの文字を置換
  for (let i = 0; i < convert_char.zenkaku_kana_dakuon.length; i++) {
    let reg_exp = new RegExp(convert_char.zenkaku_kana_dakuon.charAt(i), 'g');
    result = result.replace(
      reg_exp,
      convert_char.hankaku_kana_dakuon_base.charAt(i) + convert_char.hankaku_kana_dakuten
    );
  }

  // 半濁点ありの文字を置換
  for (let i = 0; i < convert_char.zenkaku_kana_handakuon.length; i++) {
    let reg_exp = new RegExp(convert_char.zenkaku_kana_handakuon.charAt(i), 'g');
    result = result.replace(
      reg_exp,
      convert_char.hankaku_kana_handakuon_base.charAt(i) + convert_char.hankaku_kana_handakuten
    );
  }

  return result;
};

/**
 * 全角ひらがな→半角ｶﾅ変換
 * @param original 変換するデータ
 * @return string  変換後のデータ
 */
const convertHiraganaToHalfKana = (original: string): string => {
  let result = original;

  // 濁点、半濁点なしの文字を置換
  for (let i = 0; i < convert_char.zenkaku_hiragana.length; i++) {
    let reg_exp = new RegExp(convert_char.zenkaku_hiragana.charAt(i), 'g');
    result = result.replace(reg_exp, convert_char.hankaku_kana.charAt(i));
  }

  // 濁点ありの文字を置換
  for (let i = 0; i < convert_char.zenkaku_hiragana_dakuon.length; i++) {
    let reg_exp = new RegExp(convert_char.zenkaku_hiragana_dakuon.charAt(i), 'g');
    result = result.replace(
      reg_exp,
      convert_char.hankaku_kana_dakuon_base.charAt(i) + convert_char.hankaku_kana_dakuten
    );
  }

  // 半濁点ありの文字を置換
  for (let i = 0; i < convert_char.zenkaku_hiragana_handakuon.length; i++) {
    let reg_exp = new RegExp(convert_char.zenkaku_hiragana_handakuon.charAt(i), 'g');
    result = result.replace(
      reg_exp,
      convert_char.hankaku_kana_handakuon_base.charAt(i) + convert_char.hankaku_kana_handakuten
    );
  }

  return result;
};

/**
 * 改行コードをエスケープされていない形に変換する。
 * 数値型は文字列に変換して返却する
 *
 * @param original 変換するデータ
 * @return string  変換後のデータ
 */
export const convertLine = (original: string | number): string => {
  // 数値型の場合はstring化して返却
  if (typeof original === 'number') {
    return String(original);
  } else if (typeof original !== 'string') {
    return original;
  }
  //改行コードがエスケープされている場合はエスケープなしに置換
  return original.match(/\\n/g) !== null ? original.replace(/\\n/g, '\n') : original;
};

/**
 * 10文字以下の文字列を0埋めの10桁になるように整形する
 * @param value 入力値
 * @return string 整形後のデータ
 */
export const inputAssistDigit = (value: string): string => {
  if (value !== undefined && value !== null && value !== '') {
    //10桁以下の場合は0で桁を埋める。
    if (value.length < 10) {
      const resultValue = assist_digit.slice(value.length) + value;
      return resultValue;
    }
  }
  //入力値が未定義、空文字、10文字以上の場合は成形せずに返却する。
  return value;
};

/**
 * 半角→全角変換
 * @param original 入力値
 * @return string 変換後の文字列
 */
export const convertHalfToFull = (original: string): string => {
  // 半角文字→全角文字に変換する
  let new_str = convertHalfKanaToFullKana(original);

  // 半角記号→全角記号に変換する
  return convertSymbol(new_str);
};

/**
 * 半角記号→全角記号に変換する
 * @param str 変換元文字列
 * @returns  変換後文字列
 */
const convertSymbol = (str: string): string => {
  return str
    .replace(/[!-~]/g, value => String.fromCharCode(value.charCodeAt(0) + 0xfee0))
    .replace(/[-－﹣−‐⁃‑‒–—﹘―⎯⏤ｰ─━]/g, '‐'); // NOTE 複数種類のハイフンを表示する際は1つに統一
};

/**
 * 全角記号→半角記号に変換する
 * @param str 変換元文字列
 * @return  変換後文字列
 */
const convertSymbolToHalf = (str: string): string => {
  return str
    .replace(/[！-～]/g, value => String.fromCharCode(value.charCodeAt(0) - 0xfee0))
    .replace(/[-－﹣−‐⁃‑‒–—﹘―⎯⏤─━]/g, '-'); // NOTE 複数種類のハイフンを表示する際は1つに統一
};

/**
 * 半角→口座名義(カナ)の使用文字
 * @param original 入力値
 * @return string 変換後の文字列
 */
export const convertHaltToAccountHolderZenkakuKana = (original: string): string => {
  // 半角英数を全角英数に変換する
  const new_str = convertHalfAlphaNumeralToFullAlphaNumeral(
    convertHalfNumeralToFullNumeral(
      convertFullKanaToHaltToAccountHolderFullKana(convertHiraganaToFullKana(original))
    )
  );

  // 記号を全角に変換する
  return new_str.replace(/[!-~]/g, str =>
    String.fromCharCode(str.charCodeAt(0) + utf_code.zenkaku_numeric_convert)
  );
};

/**
 * 全角カナ→口座名義(カナ)の使用文字変換
 * @param original 変換するデータ
 * @return string  変換後のデータ
 */
export const convertFullKanaToHaltToAccountHolderFullKana = (original: string): string => {
  //半角カナ,半角の記号(バックスラッシュ)を置換
  return convertToFullKana(original).replace(
    convert_char.hankaku_backslash,
    convert_char.zenkaku_backslash
  );
};

/**
 * 半角数字→全角数字変換
 * @param original 変換するデータ
 * @return string  変換後のデータ
 */
const convertHalfNumeralToFullNumeral = (original: string): string => {
  let result = original;

  // 半角数字を全角数字に置換
  for (let i = 0; i < convert_char.hankaku_numeric_base.length; i++) {
    let reg_exp = new RegExp(convert_char.hankaku_numeric_base.charAt(i), 'g');
    result = result.replace(reg_exp, convert_char.zenkaku_numeric_base.charAt(i));
  }

  return result;
};

/**
 * 半角英字→全角英字変換
 * @param original 変換するデータ
 * @return string  変換後のデータ
 */
const convertHalfAlphaNumeralToFullAlphaNumeral = (original: string): string => {
  let result = original;

  // 半角英字を全角英字に置換
  for (let i = 0; i < convert_char.hankaku_alphabet_base.length; i++) {
    let reg_exp = new RegExp(convert_char.hankaku_alphabet_base.charAt(i), 'g');
    result = result.replace(reg_exp, convert_char.zenkaku_alphabet_base.charAt(i));
  }

  return result;
};

/**
 * タイプが一致する場合、入力値からカンマを除去する
 * @param original 入力値
 * @param type 変換タイプ
 */
export const removeComma = (original: string, type: string): string => {
  if (
    type === convert_types.add_comma ||
    type === link_setting.input_assistance_name.repayment_simulation_housing ||
    type === link_setting.input_assistance_name.repayment_simulation_car ||
    type === convert_types.allow_minus
  ) {
    return original.replace(/,/g, '');
  } else {
    return original;
  }
};

/*
 * 文字列から行数を取得する。
 * @return number
 */
export const calcRow = (text: string): number => {
  const rows = text !== null ? text.split('\n') : '';

  if (rows.length > 1) {
    return rows.length + 1;
  } else {
    return textarea_default_row;
  }
};

/**
 *
 * APIにPOSTするデータを厳選、整形する
 *
 * @param fm_type 該当項目の種別
 * @param value 該当項目の入力値
 * @param input_assistance 該当項目のinput_assistance
 * @return string
 */
export const convertApiData = (fm_type: string, value: any, input_assistance: string): string => {
  const date_types = [fm_types.date, fm_types.business_date, fm_types.after_specified_date];

  return fm_type === fm_types.checkbox ||
    fm_type === fm_types.increase_months_select ||
    fm_type === fm_types.guarantor_num_linked_checkbox
    ? value.join()
    : fm_type === fm_types.postcode
    ? convertPostCode(value)
    : input_assistance === convert_types.add_comma ||
      input_assistance === link_setting.input_assistance_name.repayment_simulation_housing ||
      input_assistance === link_setting.input_assistance_name.repayment_simulation_car
    ? convertToNumericApi(value)
    : input_assistance === convert_types.allow_minus
    ? convertToNumericHyphen(value)
    : date_types.includes(fm_type) && value !== null && value !== ''
    ? Moment(value).format('YYYY-MM-DD')
    : input_assistance === convert_types.add_decimal_point
    ? convertToDecimal(value)
    : value;
};

/**
 * 郵便番号の値を適切な形式に変換する
 *
 * @param value 郵便番号の入力値
 * @return string 変換後の値
 */
const convertPostCode = (value: string[]): string => {
  if (
    value.length === 0 ||
    (value[postcode_index.first] === '' && value[postcode_index.last] === '')
  ) {
    return '';
  }
  return `${value[postcode_index.first]}${postcode_splitter.hyphen}${value[postcode_index.last]}`;
};

/**
 * 数値に変換
 * @param original 変換するデータ
 * @return string  変換後のデータ
 */
const convertToNumericApi = (original: string): string => {
  if (typeof original === 'undefined' || original === null) {
    return '';
  }

  let result = String(original);

  // 半角数値ではない場合のみ、変換処理実施
  if (!result.toString().match(/^[0-9]+$/)) {
    //半角数値
    result = convertFullNumericToHalfNumeric(result);
  }

  // 数字(0～9)以外を除去して
  result = result.toString().replace(/[^\d]/g, '');

  // comma除去処理
  result = result.toString().replace(/,/g, '');

  return result;
};

/**
 * 時, 分の結合値を時, 分の配列に変換する
 *
 * @param {string} value 時, 分の結合値
 * @return {string[]} 時, 分の配列
 */
export const splitTime = (value: string): string[] => {
  if (value === null || typeof value === 'undefined' || value.length === 0) {
    return ['', ''];
  }
  const split_times = value.split(time_splitter);
  return split_times;
};

/**
 * 2桁になっていない時 or 分に0を付与する
 *
 * @param {string} value 時 or 分
 * @return {string} 0付与した時 or 分
 */
export const timeZeroPadding = (value: string): string => {
  return ('00' + value).slice(-2);
};

/**
 * 数字(0～9)以外を除去する
 *
 * @param {string} value 入力値
 * @return {number} 変換した値
 */
export const numberExtraction = (value: string): number => {
  return Number(value.replace(/[^\d]/g, ''));
};
